import React, { useState } from 'react';
import { Paragraph } from '@arctheme-components/elements/Paragraph/Paragraph';

interface MinicartNoteProps {
  block?: any;
  settings: {
    // Content settings
    cart_note_label?: string;
    cart_note_label_style?: string;
    cart_note_label_text_color?: string;
    cart_note_label_text_style?: string;
    cart_note_placeholder?: string;
    cart_note_placeholder_style?: string;
    show_cart_note?: boolean;
    textarea_border?: string;

    // Appearance settings
    color_schema?: string;
    border_radius?: number;

    // Layout & Spacing settings
    direction?: string;
    padding_top?: number;
    padding_bottom?: number;
    padding_left_right?: number;
    margin_top?: number;
    margin_bottom?: number;
    margin_left_right?: number;

    // Divider settings
    show_divider?: boolean;
    divider_color?: string;
    divider_thickness?: number;

    // Legacy settings (for backward compatibility) - removed unused padding setting
  };
}

export const MinicartNote = ({ settings, block }: MinicartNoteProps) => {
  // Early return if cart note is disabled
  if (settings?.show_cart_note === false) {
    return null;
  }

  const [note, setNote] = useState('');

  // Content settings
  const label = settings?.cart_note_label || 'Note';
  const placeholder = settings?.cart_note_placeholder || 'Add special instructions for your order';
  const labelTextStyle =
    settings?.cart_note_label_text_style || settings?.cart_note_label_style || 'text-sm font-medium text-gray-700';

  // Layout & Spacing
  const direction = settings?.direction || 'column';
  const paddingTop = settings?.padding_top ?? 0;
  const paddingBottom = settings?.padding_bottom ?? 0;
  const paddingLeftRight = settings?.padding_left_right ?? 0;
  const marginTop = settings?.margin_top ?? 0;
  const marginBottom = settings?.margin_bottom ?? 20;
  const marginLeftRight = settings?.margin_left_right ?? 0;

  // Appearance
  const borderRadius = settings?.border_radius || 0;
  const textareaBorder = settings?.textarea_border || '1px solid #e5e7eb';

  // Divider settings
  const showDivider = settings?.show_divider || false;
  const dividerColor = settings?.divider_color || '#e5e7eb';
  const dividerThickness = settings?.divider_thickness || 1;

  // Legacy support - removed unused padding setting

  const handleNoteChange = (e) => {
    const value = e.target.value;
    setNote(value);

    // Update cart note using the ajax cart API
    const event = new CustomEvent('liquid-ajax-cart:request', {
      detail: {
        request: {
          url: '/cart/update.js',
          method: 'POST',
          body: {
            note: value
          }
        }
      }
    });

    document.dispatchEvent(event);
  };

  // Container styles
  const containerStyle = {
    paddingTop: `${paddingTop}px`,
    paddingBottom: `${paddingBottom}px`,
    paddingLeft: `${paddingLeftRight}px`,
    paddingRight: `${paddingLeftRight}px`,
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    marginLeft: `${marginLeftRight}px`,
    marginRight: `${marginLeftRight}px`,
    borderRadius: `${borderRadius}px`,
    display: 'flex',
    flexDirection: direction === 'row' ? 'row' : 'column',
    gap: direction === 'row' ? '10px' : '4px'
  } as React.CSSProperties;

  // Get the correct color schema from block data
  const colorSchema = block?.color_schema || settings?.color_schema;

  return (
    <div
      className={`minicart-note ${colorSchema ? `color-${colorSchema}` : ''}`}
      style={containerStyle}
    >
      <label
        htmlFor='cart-note'
        className='block'
      >
        <Paragraph
          classes={labelTextStyle}
          text={label}
        />
      </label>
      <textarea
        id='cart-note'
        name='note'
        rows={2}
        className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary'
        placeholder={placeholder}
        value={note}
        onChange={handleNoteChange}
      ></textarea>

      {showDivider && (
        <div
          style={{
            width: '100%',
            height: dividerThickness,
            backgroundColor: dividerColor
          }}
        />
      )}
    </div>
  );
};
